import React, { useState, useEffect } from "react";
import { Box, Typography, Card, Container, useTheme } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import FundsTable from "./FundsTable";
import FundsDialog from "./FundsDialog";
import SMADialog from "./SMADialog";
import EditSMADialog from "./EditSMADialog";
import { Helmet } from "react-helmet";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../Redux/Reducers/uiSlice";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import {selectFunds, selectFundsLoading, selectFundsError } from "../../Redux/Reducers/selectors";

const Funds = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  const [openSMADialog, setOpenSMADialog] = useState(false);
  const [openEditSMADialog, setOpenEditSMADialog] = useState(false);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [smaForEdit, setSmaForEdit] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);

  // Use selectors directly from funds slice
  const funds = useSelector(selectFunds);
  const fundsLoading = useSelector(selectFundsLoading);
  const fundsError = useSelector(selectFundsError);
 

  // Still get accountingData from old state for now

  const handleAdd = () => {
    setRecordForEdit(null);
    setOpenDialog(true);
  };

  const handleAddSMA = () => {
    setOpenSMADialog(true);
  };

  const handleEdit = (fund) => {
    // Check if it's an SMA
    if (fund.isSMA) {
      setSmaForEdit(fund);
      setOpenEditSMADialog(true);
    } else {
      setRecordForEdit(fund);
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setRecordForEdit(null);
  };

  const handleCloseSMADialog = () => {
    setOpenSMADialog(false);
  };

  const handleCloseEditSMADialog = () => {
    setOpenEditSMADialog(false);
    setSmaForEdit(null);
  };

  const handleSave = (fund) => {
    if (!fund || Object.keys(fund).length === 0) return;

    if (recordForEdit) {
      // sendWebSocketMessage(
      //   `{"action":"modify_fund", "data":{"fund":${JSON.stringify({
      //     ...fund,
      //     fundId: fund.fundId,
      //     creationDate: fund.creationDate,
      //   })}}}`
      // );
      // dispatch(addActiveRequests("modify_fund"));
      // dispatch(updateLoading(true));
      // dispatch(updateLoadingMsg("Updating Fund..."));
    } else {
      fund.abbreviation = fund.abbreviation.toLowerCase();
      if (
        funds?.find((a) => {
          if (a.fundId !== fund.fundId)
            return a.abbreviation === fund.abbreviation;
        })
      ) {
        alert(`${fund.abbreviation} already exists, try new one.`);
        return;
      }
      sendWebSocketMessage(
        `{"action":"add_fund", "data":${JSON.stringify(fund)}}`
      );
      dispatch(addActiveRequests("add_fund"));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("Adding Fund..."));
    }
    handleCloseDialog();
  };

  const handleSaveSMA = (smaData) => {
    if (!smaData || Object.keys(smaData).length === 0) return;

     smaData.abbreviation = smaData.abbreviation.toLowerCase();
    // Check if abbreviation already exists
    if (
      funds?.find((a) => a.abbreviation === smaData.abbreviation)
    ) {
      alert(`${smaData.abbreviation} already exists, try new one.`);
      return;
    }

    
    
    sendWebSocketMessage(
      `{"action":"new_sma", "data":${JSON.stringify(smaData)}}`
    );
    dispatch(addActiveRequests("new_sma"));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Adding SMA..."));
    
    handleCloseSMADialog();
  };

  const handleUpdateSMA = (smaData) => {
    if (!smaData || Object.keys(smaData).length === 0) return;

    smaData.abbreviation = smaData.abbreviation.toLowerCase();
    
    // Check if abbreviation already exists (excluding current SMA)
    if (
      funds?.find((a) => a.fundId !== smaData.smaId && a.abbreviation === smaData.abbreviation)
    ) {
      alert(`${smaData.abbreviation} already exists, try new one.`);
      return;
    }

    // Only send abbreviation, account, and smaId
    const requestPayload = {
      action: "update_sma",
      data: {
        abbreviation: smaData.abbreviation,
        account: smaData.account,
        smaId: smaData.smaId
      }
    };
    

    sendWebSocketMessage(JSON.stringify(requestPayload));
    dispatch(addActiveRequests("update_sma"));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Updating SMA..."));
    
    handleCloseEditSMADialog();
  };

  const handleDelete = (fund) => {
    if (window.confirm("Are you sure you want to delete this fund?")) {
      // sendWebSocketMessage(
      //   `{"action":"delete_fund", "data":{"fundId":"${fund}"}}`
      // );
      // dispatch(addActiveRequests("delete_fund"));
      // dispatch(updateLoading(true));
      // dispatch(updateLoadingMsg("Deleting Fund..."));
      // dispatch(addActiveRequests("delete_fund"));
    }
  };

  // Show error state if there's an error
  if (fundsError) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.grey[50],
          minHeight: "100vh",
          py: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="h6" color="error">
          Error loading funds: {fundsError}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Funds | Vantage Alpha</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
          background: theme.palette.mode == "dark" ? theme.palette.grey[50] : '#ebebeb',
          minHeight: "100vh",
          py: 3,
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 1,
              }}
            >
              Funds
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
              }}
            >
              Manage fund configurations and account relationships
              {fundsLoading && " (Loading...)"}
            </Typography>
          </Box>

          <Card
            sx={{
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <FundsTable
              funds={funds || []}
              onAdd={handleAdd}
              onAddSMA={handleAddSMA}
              onEdit={handleEdit}
              onDelete={handleDelete}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />
          </Card>
        </Container>

        <FundsDialog
          open={openDialog}
          onClose={handleCloseDialog}
          onSave={handleSave}
          fund={recordForEdit}
        />

        <SMADialog
          open={openSMADialog}
          onClose={handleCloseSMADialog}
          onSave={handleSaveSMA}
        />

        <EditSMADialog
          open={openEditSMADialog}
          onClose={handleCloseEditSMADialog}
          onSave={handleUpdateSMA}
          sma={smaForEdit}
        />
      </Box>
    </>
  );
};

export default Funds;